import { cloneElement, type JSX, useContext, useState } from 'react'
import { MoreVert } from '@mui/icons-material'
import { IconButton, Menu, MenuItem, useTheme } from '@mui/material'
import { useSnackbar } from 'notistack'

import MoveFolderModal from '../../../../components/change-folder-modals/move-folder-modal'
import ExternalLinkMenuItem from '../../../../components/external-link-menu-item'
import { AppContext } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import { INSIGHTS_URL, PROTECT_LITE_URL } from '../../../../utils/constants'
import { openSensorStore } from '../../../../utils/helpers'
import useStyles from '../../index.styles'

import ChangeNameStatusModal from './change-name-status-modal'
import ConfirmOptOutModal from './confirm-opt-out-modal'
import ProtectMobileDialog from './protect-mobile-dialog'
import ReactivateFolderModal from './reactivate-folder-modal'

interface IProps {
  folderId: number
  folderName: string
  showStoreLink: boolean
  menuButtonElement?: JSX.Element
  folderType?: FolderType
  showOptOutMenu: boolean
  showReactivateMenu: boolean
}

const QuickActionsMenu = (props: IProps) => {
  const { folderId, folderName, showStoreLink, menuButtonElement, folderType, showOptOutMenu, showReactivateMenu } = props
  const { enqueueSnackbar } = useSnackbar()
  const { tree, host } = useContext(AppContext)

  const { auth, hasInternalUserAccess } = useAuthentication()

  const folderParentId = tree && tree[folderId] ? tree[folderId].parentId : undefined

  const classes = useStyles
  const theme = useTheme()

  const [protectMobileDialog, setProtectMobileDialog] = useState<boolean>(false)
  const [showChangeNameStatusModal, setShowChangeNameStatusModal] = useState<boolean>(false)
  const [showMoveFolderModal, setShowMoveFolderModal] = useState<boolean>(false)
  const [showReactivateFolderModal, setShowReactivateFolderModal] = useState<boolean>(false)
  const [showConfirmOptOutModal, setShowConfirmOptOutModal] = useState<boolean>(false)

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)

  const folderParentName = tree && folderParentId && tree[folderParentId] ? tree[folderParentId]?.name : undefined
  const menuOpen = Boolean(menuAnchor)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  const customMenuButton = menuButtonElement ? cloneElement(menuButtonElement, { onClick: handleMenuOpen }) : undefined

  return (
    <div style={{ ...(!menuButtonElement ? classes.menuContainer : {}) }}>
      {!customMenuButton ? (
        <IconButton
          size="small"
          sx={{ ...classes.menuButton, ...(menuOpen ? classes.moreOptionsBtnSelected(theme) : {}) }}
          onClick={handleMenuOpen}
          aria-label="Toggle quick actions menu"
        >
          <MoreVert />
        </IconButton>
      ) : (
        customMenuButton
      )}

      <Menu
        anchorEl={menuAnchor}
        id="quick-actions-menu"
        open={menuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {folderType !== 'Program' && hasInternalUserAccess && (
          <MenuItem
            onClick={() => {
              setShowChangeNameStatusModal(true)
              handleMenuClose()
            }}
          >
            Change Name or Status
          </MenuItem>
        )}

        {folderType !== 'Program' && folderType !== 'Client Company' && folderName && folderParentName && hasInternalUserAccess && (
          <MenuItem
            onClick={() => {
              setShowMoveFolderModal(true)
              handleMenuClose()
            }}
          >
            Move Folder
          </MenuItem>
        )}

        {showReactivateMenu && (
          <MenuItem
            onClick={() => {
              setShowReactivateFolderModal(true)
              handleMenuClose()
            }}
          >
            Reactivate
          </MenuItem>
        )}

        {showOptOutMenu && hasInternalUserAccess && (
          <MenuItem
            onClick={() => {
              setShowConfirmOptOutModal(true)
              handleMenuClose()
            }}
          >
            Opt out
          </MenuItem>
        )}
        <ExternalLinkMenuItem href={`${INSIGHTS_URL}dashboard?folderIds=${folderId}`} label="View in Insights" />

        {hasInternalUserAccess && <ExternalLinkMenuItem href={`${PROTECT_LITE_URL}/dashboard/${folderId}`} label="View in Protect Lite" />}

        {folderType !== 'Program' && hasInternalUserAccess && (
          <MenuItem
            onClick={() => {
              setProtectMobileDialog(true)
              handleMenuClose()
            }}
          >
            View in Protect Mobile
          </MenuItem>
        )}

        {showStoreLink && folderType !== 'Program' && auth.user?.access_token && hasInternalUserAccess && (
          <ExternalLinkMenuItem
            onClick={() => {
              openSensorStore(folderId, enqueueSnackbar, auth.user!.access_token, host)
              handleMenuClose()
            }}
            label="View Sensor Store"
          />
        )}
      </Menu>

      <ProtectMobileDialog folderId={folderId} open={protectMobileDialog} setOpen={setProtectMobileDialog} />

      <ConfirmOptOutModal open={showConfirmOptOutModal} setOpen={setShowConfirmOptOutModal} folderId={folderId} folderName={folderName} />

      <ReactivateFolderModal open={showReactivateFolderModal} setOpen={setShowReactivateFolderModal} folderId={folderId} folderName={folderName} />

      <ChangeNameStatusModal open={showChangeNameStatusModal} setOpen={setShowChangeNameStatusModal} folderName={folderName} folderId={folderId} />

      {folderName && folderParentName && (
        <MoveFolderModal
          open={showMoveFolderModal}
          onClose={() => setShowMoveFolderModal(false)}
          currentFolderId={folderId}
          currentFolderName={folderName}
          currentParentFolderName={folderParentName}
        />
      )}
    </div>
  )
}

export default QuickActionsMenu
